<script setup lang="ts">
import type { FormKitFrameworkContext } from '@formkit/core'
import Multiselect from '@vueform/multiselect'
import ProductOptionSlot from './select/ProductOption.slot.vue'
import ProductOptionCompactSlot from './select/ProductOptionCompact.slot.vue'

const props = defineProps<{
  context: FormKitFrameworkContext
}>()
const state = ref({
  loaded: false,
  loadedOptions: false,
  loading: false,
  version: 1,
})
const selectRef = ref(null)

const config: any = {
  mode: 'single',
  type: 'text',
  variant: 'default',
  view: 'default',
  showAddAll: false,
  placeholder: '',
  delay: 100,
  canClear: true,
  canDeselect: false,
  filterResults: true,
  keepValuesOnWatch: false,
  resolveOnLoad: true,
  searchable: true,
  trackBy: undefined,
  groups: false,
  object: false,
  watch: [],
  ...props.context,
}

function handleChange(value: any) {
  if (areEqual(value, props.context.value)) {
    return
  }
  props.context.node.input(value === false ? false : value || null)
  if (selectRef.value) selectRef.value.search = ''
  selectRef.value && selectRef.value.blur()
}

function handleSearch(query) {
  if (!(config.options instanceof Function) && config.initialOptions) {
    const initialOptions = config.initialOptions() || []
    return initialOptions.filter((option) => {
      if (!query) return true
      const searchStr = query.toLowerCase()
      const label = option.label?.toLowerCase() || ''
      const value = String(option.value)?.toLowerCase() || ''
      return label.includes(searchStr) || value.includes(searchStr)
    })
  }
}

const root = props.context.node.at('$root')

const contextData = (config.watch || []).reduce(
  (acc, name) => ({
    [name]: root.at(name)?.value,
    ...acc,
  }),
  {},
)

const disabledValue = ref(
  config.attrs.watchDisabled && config.attrs.watchDisabled(root.value),
)

watch(
  () => (root.value as any)?.channelId,
  () => {
    if (config.attrs.watchDisabled) {
      const currentDisabled = config.attrs.watchDisabled(root.value)
      if (disabledValue.value !== currentDisabled) {
        disabledValue.value = currentDisabled
      }
    }
  },
)
// console.log("Watcher available: ", config.watcher)
if (config.watcher) {
  if (!config.keepValuesOnWatch) {
    config.watcher.addHandler('flushOnChange', (current, previous) => {
      // console.log("Running flush on change", JSON.stringify(current))
        handleChange(null)
    })
  }

  if (config.keepValuesOnWatch) {
    config.watcher.addHandler('flushOnEmpty', (current, previous) => {
      // console.log("Running flush on empty", JSON.stringify(current))

      if (!current || !current.length) {
        handleChange(null)
      }
    })
  }

  config.watcher.addHandler(
    'default',
    (current, previous, mainState: Ref<any>) => {
      // console.log("Running default handler")
      const v = config.nameValue ? mainState.value[config.nameValue] : null
      nextTick(() => {
        if (!selectRef.value.internalValue?.value?.length && v && v.length) {
          state.value.version++
          // selectRef.value?.refreshOptions()
          // if (v || (Array.isArray(v) && v.length)) {

          //   // state.value[config.nameValue] = null
          //   // nextTick(() => state.value[config.nameValue] = v)

          //   // setTimeout(() => {
          //   //     // if (JSON.stringify(state.value[config.nameValue]) !== JSON.stringify(v)) {
          //   //       console.log("SETTING VALUE", config.nameValue, v)
          //   //       nextTick(() => state.value[config.nameValue] = v)

          //   //     // }
          //   // }, 1000 + Math.random() * 20)
          // }
        }
      })
    },
  )
}

if (!config.watcher) {
  ;(config.watch || []).forEach((name) => {
    root.at(name)?.on('input', ({ payload: value }) => {
      // When watch is triggered we want to verify that watched values were changed
      // If they were, we should do a request that includes the existing values
      const prev = contextData[name]
      if (value === undefined || areEqual(prev, value)) {
        return
      }
      if (!config.keepValuesOnWatch && prev !== value) {
        handleChange(null)
      }
      if (config.keepValuesOnWatch && (!value || !value.length)) {
        handleChange(null)
      }

      if (prev !== value) {
        contextData[name] = value
        selectRef.value?.refreshOptions()
      }
    })
  })
}

async function options(value?: string) {
  state.value.loading = true
  const where = config.where ? config.where(contextData) : contextData
  let result
  if (
    config.initialOptions &&
    !value &&
    config.resolveOnLoad &&
    !state.value.loaded
  ) {
    const initialValues =
      config.mode === 'single'
        ? [props.context._value].filter(Boolean)
        : props.context._value
    state.value.loaded = true
    result = (
      await Promise.all([
        config.initialOptions(initialValues, where),
        config.options(null, where),
      ])
    ).flat()
    state.value.loading = false
    return result.reduce((acc, v) => {
      if (acc.findIndex((a) => a.value === v.value) < 0) {
        acc.push(v)
      }
      return acc
    }, [])
    // return config.initialOptions(initialValues, where)
  } else if (config.options instanceof Function) {
    if (!value?.length) {
      state.value.loadedOptions = true
    }

    result = await config.options(value, where)
  } else {
    result = config.options
  }
  state.value.loading = false

  return result
}

const onClear = (clearSelect: Function, event) => {
  clearSelect(event)
  if (!state.value.loadedOptions) {
    options()
  }
}

// Not sure this block is good. Made for filters clear button
watch(
  () => props.context.value,
  (v) => {
    if (props.context._value === undefined || v === null) {
      selectRef.value && selectRef.value.clear()
      selectRef.value?.resolveOptions()
    }
  },
)

const onAddAll = () => {
  // When in single mode, we don't want to add all
  if (config.mode === 'single') return

  // Get all available options with their full structure
  const values = selectRef.value.extendedOptions.map((o) =>
    config.object
      ? {
          value: o.value,
          label: o.label,
          $groupLabel: o.$groupLabel,
          ...o,
        }
      : o.value,
  )

  handleChange(values)
}
</script>

<template>
  <div class="flex flex-row align-middle justify-center flex-1">
    <Multiselect
      ref="selectRef"
      :model-value="context._value"
      :mode="config.mode"
      :loading="state.loading"
      :options="options"
      :can-clear="config.canClear"
      :can-deselect="config.canDeselect"
      :searchable="config.searchable && !disabledValue"
      :track-by="config.trackBy"
      :filter-results="config.filterResults"
      :resolve-on-load="config.resolveOnLoad"
      :placeholder="config.placeholder"
      :delay="config.delay"
      :object="config.object"
      :groups="config.groups"
      :class="config.size && `size-${config.size}`"
      :disabled="context.disabled || config.disabled || disabledValue"
      :clear-on-select="!(config.mode === 'tags' || config.mode === 'multiple')"
      :close-on-select="!(config.mode === 'tags' || config.mode === 'multiple')"
      :infinite="false"
      :limit="0"
      :key="state.version?.toString()"
      @input="handleChange"
      @open="(select$: any) => select$ && select$.noOptions && select$.resolveOptions()"
      @search-change="handleSearch"
    >
      <template
        v-for="(slot, slotName) in (context.slots as any[])"
        :key="slotName"
        #[slotName.toString()]="data"
      >
        <component :is="slot.bind(null, data)" />
      </template>
      <template #caret>
        <div class="multiselect-caret">
          <div class="i-heroicons-solid-selector multiselect-caret-icon" />
        </div>
      </template>
      <template #clear="{ clear }">
        <div class="multiselect-clear">
          <div
            class="i-heroicons-solid-x multiselect-clear-icon"
            @click.stop.prevent="onClear(clear, $event)"
          />
        </div>
      </template>
      <template #spinner>
        <Spinner class="multiselect-spinner z-30" />
      </template>
      <template #singlelabel="{ value }">
        <div
          v-if="config.view === 'product'"
          class="multiselect-single-label !relative"
        >
          <ProductOptionSlot
            v-if="!selectRef.isActive"
            :product="value"
            class="z-10 w-full p-2"
          />
        </div>
        <div
          v-if="config.view === 'product-compact'"
          class="multiselect-single-label !relative"
        >
          <ProductOptionCompactSlot
            v-if="!selectRef.isActive"
            :product="value"
            class="z-10 w-full p-2"
          />
        </div>
        <slot v-else name="singlelabel" :value="value" />
      </template>
      <template #option="{ option }">
        <ProductOptionSlot v-if="config.view === 'product'" :product="option" />
        <ProductOptionCompactSlot
          v-else-if="config.view === 'product-compact'"
          :product="option"
        />
        <div
          v-else-if="config?.attrs?.attrs?.activated?.includes(option.value)"
        >
          <span>{{ option.label }}</span>
          <Badge class="ml-2" :check="true" :type="'success'" />
        </div>
        <slot v-else name="option" :option="option" />
      </template>
      <template #tag="{ option, handleTagRemove, disabled }">
        <span
          v-if="config.view === 'fulltag'"
          class="multiselect-tag-full"
          :class="{
            '!max-w-none w-full flex justify-between !p-2':
              config.view === 'product',
            '!max-w-full': config.view === 'longtag',
          }"
        >
          <ProductOptionSlot
            v-if="config.view === 'product'"
            :product="option"
          />
          <ProductOptionCompactSlot
            v-else-if="config.view === 'product-compact'"
            :product="option"
          />
          <span v-else class="truncate">
            {{ option.label }}
          </span>
          <span
            v-if="!disabled"
            class="multiselect-tag-remove group"
            @click.prevent="handleTagRemove(option, $event)"
          >
            <div class="i-heroicons-solid-x multiselect-tag-remove-icon" />
          </span>
        </span>
        <span
          v-else
          class="multiselect-tag"
          :class="{
            '!max-w-none w-full flex justify-between !p-2':
              config.view === 'product',
            '!max-w-full': config.view === 'longtag',
          }"
        >
          <ProductOptionSlot
            v-if="config.view === 'product'"
            :product="option"
          />
          <ProductOptionCompactSlot
            v-else-if="config.view === 'product-compact'"
            :product="option"
          />
          <span v-else class="truncate">
            {{ option.label }}
          </span>
          <span
            v-if="!disabled"
            class="multiselect-tag-remove group"
            @click.prevent="handleTagRemove(option, $event)"
          >
            <div class="i-heroicons-solid-x multiselect-tag-remove-icon" />
          </span>
        </span>
      </template>
    </Multiselect>
    <Button
      @click="onAddAll()"
      class="inline-block text-xs !p-0 !px-2"
      variant="simple"
      v-if="context.attrs.showAddAll"
      >Fill All</Button
    >
  </div>
</template>

<style lang="postcss" scoped>
.multiselect-wrapper {
  @apply relative mx-auto w-full flex items-center justify-end box-border cursor-pointer outline-none py-2;
}
.multiselect-assistive-text {
  @apply absolute -m-px w-px h-px overflow-hidden;
  clip: rect(0 0 0 0);
}
.multiselect {
  @apply relative flex-grow flex items-center justify-end box-border cursor-pointer text-gray-800 rounded-lg bg-white text-sm leading-snug outline-none;
}
.has-error .multiselect {
  @apply border-red-600;
}
.multiselect.size-small {
  @apply text-xs;
}
.multiselect.is-disabled {
  @apply cursor-default bg-gray-100;
}
/* .multiselect.is-active {
  @apply ring-1 ring-primary-500 border-primary-500 outline-none;
} */
.multiselect-multiple-label,
.multiselect-single-label {
  @apply flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug px-3.5 text-sm;
}
.multiselect-single-label {
  @apply w-full;
  .multiselect-single-label-text {
    @apply overflow-hidden text-ellipsis whitespace-nowrap pr-9;
  }
}
.multiselect-search {
  @apply w-full absolute inset-0 outline-none appearance-none border-none focus:outline-none focus:ring-0 focus:z-10 box-border text-sm font-sans bg-white rounded-lg pl-3.5 !important;
}
.multiselect-tags {
  @apply flex-grow flex-shrink flex flex-wrap items-center mt-1 pl-2;
}
.multiselect-tag {
  @apply bg-gray-100 text-gray-700 max-w-[15rem] text-xs font-semibold py-0.5 pl-2 rounded mr-1 mb-1 flex items-center whitespace-nowrap;
}
.multiselect-tag-full {
  @apply bg-gray-100 text-gray-700 max-w-[40rem] text-xs font-semibold py-0.5 pl-2 rounded mr-1 mb-1 flex items-center whitespace-nowrap;
}
.multiselect-tag.is-disabled {
  @apply pr-2 opacity-50;
}
.multiselect-tag-remove {
  @apply flex items-center justify-center p-1 mx-0.5 rounded-sm hover:bg-black hover:bg-opacity-5;
}
.multiselect-tag-remove-icon {
  @apply opacity-30 inline-block w-3 h-3 group-hover:opacity-70;
}
.multiselect-tags-search-wrapper {
  @apply inline-block relative mx-1 mb-1 flex-grow flex-shrink h-full;
}
.multiselect-tags-search {
  @apply absolute inset-0 border-0 outline-none appearance-none focus:outline-none focus:ring-0 p-0 text-sm font-sans box-border w-full !important;
}
.multiselect-tags-search-copy {
  @apply invisible whitespace-pre-wrap inline-block h-px;
}
.multiselect-placeholder {
  @apply flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 pr-5 text-sm text-gray-400;
}
.multiselect-caret {
  @apply flex items-center bg-center bg-no-repeat h-4 py-px box-content mr-3.5 relative opacity-40 flex-shrink-0 flex-grow-0 transition-transform transform pointer-events-none;
}
.multiselect-caret.is-open {
  @apply rotate-180 pointer-events-auto;
}
.multiselect-clear {
  @apply pr-2 relative opacity-40 transition duration-300 flex-shrink-0 flex-grow-0 flex hover:opacity-80;
}
.multiselect-clear-icon {
  @apply w-4 h-4 py-px box-content inline-block;
}
.multiselect-spinner {
  @apply w-4 h-4 mr-3.5 flex-shrink-0 flex-grow-0 text-gray-600;
}
.multiselect-dropdown {
  @apply max-h-60 absolute -left-px -right-px -bottom-1 transform translate-y-full border border-gray-200 overflow-y-auto z-30 py-1 mt-1 bg-white flex flex-col rounded-lg shadow-lg;
}
.multiselect-dropdown.is-top {
  @apply -translate-y-full top-px bottom-auto flex-col-reverse;
}
.multiselect-dropdown.is-hidden {
  @apply hidden;
}
.multiselect-options {
  @apply flex flex-col p-0 m-0 list-none;
}
.multiselect-options.is-top {
  @apply flex-col-reverse;
}
.multiselect-group {
  @apply relative p-0 m-0;
}
.multiselect-group-label {
  @apply flex sticky -top-1 text-sm box-border items-center justify-start text-left uppercase text-xs text-gray-400 py-1.5 px-3 font-semibold bg-gray-50 cursor-default leading-normal z-10 !important;
}
.multiselect-group-label.is-pointable {
  @apply cursor-pointer;
}
.multiselect-group-label.is-pointed {
  @apply bg-gray-300 text-gray-700;
}
.multiselect-group-label.is-selected {
  @apply bg-green-600 text-white;
}
.multiselect-group-label.is-disabled {
  @apply bg-gray-100 text-gray-300 cursor-not-allowed;
}
.multiselect-group-label.is-selected.is-pointed {
  @apply bg-green-600 text-white opacity-90;
}
.multiselect-group-label.is-selected.is-disabled {
  @apply bg-opacity-50 cursor-not-allowed;
}
.multiselect-group-options {
  @apply p-0 m-0;
}
.multiselect-option {
  @apply flex items-center justify-start box-border text-left cursor-pointer text-sm leading-snug py-2 px-3;
}
.multiselect-option.is-pointed {
  @apply text-gray-800 bg-gray-100;
}
.multiselect-option.is-selected {
  @apply text-gray-800 bg-white font-semibold;
}
.multiselect-option.is-disabled {
  @apply text-gray-400 cursor-not-allowed hover:text-gray-700 !important;
}
.multiselect-option.is-selected.is-pointed {
  @apply text-gray-800 bg-gray-100;
}
.multiselect-option.is-selected.is-disabled {
  @apply bg-opacity-50 cursor-not-allowed;
}
.multiselect-no-options,
.multiselect-no-results {
  @apply py-2 px-3 text-gray-600 bg-white;
}
.multiselect-fake-input {
  @apply bg-transparent absolute left-0 right-0 -bottom-px w-full h-px border-0 p-0 appearance-none outline-none text-transparent;
}
.multiselect-spacer {
  @apply h-[2.375rem] py-px box-content;
}
.multiselect.size-small .multiselect-spacer {
  @apply h-[2.125rem];
}
</style>
